import React, {useState, useEffect} from 'react'

import {Button, CheckPicker, Form, Message, Modal, toaster} from 'rsuite'

import {
  Permission,
  useCreateUserRoleMutation,
  usePermissionListQuery,
  FullUserRoleFragment,
  useUpdateUserRoleMutation,
  useUserRoleQuery,
} from '../api'

import {useTranslation} from 'react-i18next'

export interface UserRoleEditPanelProps {
  id?: string

  onClose?(): void
  onSave?(userRole: FullUserRoleFragment): void
}

export function UserRoleEditPanel({id, onClose, onSave}: UserRoleEditPanelProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [systemRole, setSystemRole] = useState(false)
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [allPermissions, setAllPermissions] = useState<Permission[]>([])

  const {
    data,
    loading: isLoading,
    error: loadError,
  } = useUserRoleQuery({
    variables: {id: id!},
    fetchPolicy: 'network-only',
    skip: id === undefined,
  })

  const {
    data: permissionData,
    loading: isPermissionLoading,
    error: loadPermissionError,
  } = usePermissionListQuery({
    fetchPolicy: 'network-only',
  })

  const [createUserRole, {loading: isCreating, error: createError}] = useCreateUserRoleMutation()
  const [updateUserRole, {loading: isUpdating, error: updateError}] = useUpdateUserRoleMutation()

  const isDisabled =
    systemRole ||
    isLoading ||
    isPermissionLoading ||
    isCreating ||
    isUpdating ||
    loadError !== undefined

  const {t} = useTranslation()

  useEffect(() => {
    if (data?.userRole) {
      setName(data.userRole.name)
      setDescription(data.userRole.description ?? '')
      setSystemRole(data.userRole.systemRole)
      setPermissions(data.userRole.permissions)
    }
  }, [data?.userRole])

  useEffect(() => {
    if (permissionData?.permissions) {
      setAllPermissions(permissionData.permissions)
    }
  }, [permissionData?.permissions])

  useEffect(() => {
    const error =
      loadError?.message ??
      createError?.message ??
      updateError?.message ??
      loadPermissionError?.message
    if (error) {
      toaster.push(
        <Message closable type="error">
          {error}
        </Message>,
        {duration: 0}
      )
    }
  }, [loadError, createError, updateError, loadPermissionError])

  // A refused save rejects the mutation. The effect on createError and updateError shows
  // the API's message; the rejection is caught so it does not surface as an uncaught error.
  const refused = () => ({data: undefined})

  async function handleSave() {
    if (id) {
      const {data} = await updateUserRole({
        variables: {
          id,
          input: {
            name,
            description,
            permissionIDs: permissions.map(({id}) => id),
          },
        },
      }).catch(refused)

      if (data?.updateUserRole) onSave?.(data.updateUserRole)
    } else {
      const {data} = await createUserRole({
        variables: {
          input: {
            name,
            description,
            permissionIDs: permissions.map(({id}) => id),
          },
        },
      }).catch(refused)

      if (data?.createUserRole) onSave?.(data.createUserRole)
    }
  }

  return (
    <>
      <Modal.Header>
        <Modal.Title>
          {id ? t('userRoles.panels.editUserRole') : t('userRoles.panels.createUserRole')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form fluid={true}>
          <Form.Group>
            <Form.ControlLabel>{t('userRoles.panels.name')}</Form.ControlLabel>
            <Form.Control
              name={t('userRoles.panels.name')}
              value={name}
              disabled={isDisabled}
              onChange={(value) => setName(value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>{t('userRoles.panels.description')}</Form.ControlLabel>
            <Form.Control
              name={t('userRoles.panels.description')}
              value={description}
              disabled={isDisabled}
              onChange={(value) => setDescription(value)}
            />
          </Form.Group>
          {systemRole && <p>{t('userRoles.panels.systemRole')}</p>}
          <Form.Group>
            <Form.ControlLabel>{t('userRoles.panels.permissions')}</Form.ControlLabel>
            <CheckPicker
              block={true}
              disabledItemValues={systemRole ? allPermissions.map((per) => per.id) : []}
              value={permissions.map((per) => per.id)}
              data={allPermissions.map((permission) => ({
                value: permission.id,
                label: permission.description,
              }))}
              onChange={(value) => {
                // setPermissions(value)
                setPermissions(
                  allPermissions.filter((permissions) => value.includes(permissions.id))
                )
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer classPrefix="wep-modal-footer">
        <Button appearance={'primary'} disabled={isDisabled} onClick={() => handleSave()}>
          {id ? t('userRoles.panels.save') : t('userRoles.panels.create')}
        </Button>
        <Button appearance={'subtle'} onClick={() => onClose?.()}>
          {t('userRoles.panels.close')}
        </Button>
      </Modal.Footer>
    </>
  )
}
